// BetDetail
//
// A pending Message Bet, shown in the main messaging pane.
//
// Clicking one of these used to call navigate('/swipe') — which App.tsx
// redirects to /social-feed — so tapping a bet in your inbox threw you out of
// messaging entirely and landed you somewhere unrelated, with nothing on
// screen about the bet you had just asked to see. For an incoming bet it was
// worse than jarring: the response UI lives on the swipe hub, and the redirect
// meant you never reached it, so there was no way to answer from here at all.
//
// Now the bet opens where you already are, beside the conversation list, in
// the same slot a conversation would occupy.

import React, { useState } from 'react';
import styled from 'styled-components';
import { MatchRequest, BetLevel } from '../../../types/swipe';
import { MatchResponse } from '../../Swipe/MatchResponse';
import { swipeService } from '../../../services/Swipe/swipeService';
import { theme } from '../../../styles/theme';
import { logger } from '../../../utils/logger';

const Panel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: ${theme.spacing.lg};
  gap: ${theme.spacing.md};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const BetIcon = styled.div`
  font-size: 2.5rem;
  line-height: 1;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 0.25rem 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const StatusLine = styled.div<{ $actionable: boolean }>`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => (props.$actionable ? theme.colors.primary : 'var(--text-secondary)')};
`;

const Card = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
`;

const CardLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  margin-bottom: ${theme.spacing.xs};
`;

const CardText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-primary);
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.lg};
`;

const MetaItem = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);

  strong {
    display: block;
    color: var(--text-primary);
    font-weight: 600;
    margin-top: 2px;
  }
`;

const WaitingNote = styled.div`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ErrorNote = styled(WaitingNote)`
  color: ${theme.colors.error};
`;

interface BetDetailProps {
  bet: MatchRequest;
  currentUserId: string;
  /**
   * The bet was answered. Carries the updated request so the host can open the
   * conversation a called bet just created.
   */
  onResponded?: (updated: MatchRequest) => void;
}

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return 'Unknown';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const formatCountdown = (iso: string | null | undefined): string => {
  if (!iso) return 'No expiry';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h left`;
  if (hours >= 1) return `${hours}h left`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m left`;
};

export const BetDetail: React.FC<BetDetailProps> = ({ bet, currentUserId, onResponded }) => {
  const [error, setError] = useState<string | null>(null);

  const iSent = bet.senderId === currentUserId;
  const otherName = iSent
    ? (bet.receiverProfile?.name || 'Someone')
    : (bet.senderProfile?.name || 'Someone');

  // Same rule the list uses to pick its icon: a bet is yours to answer when it
  // is pending and addressed to you, or countered and bounced back to you.
  const actionable = (bet.status === 'PENDING' && bet.receiverId === currentUserId)
    || (bet.status === 'COUNTER' && bet.senderId === currentUserId);

  const level = (bet.response.counterBet || bet.betLevel || '').toUpperCase();
  const message = bet.response.counterMessage || bet.message || bet.initialMessage;

  const handleResponse = async (
    action: 'accept' | 'decline' | 'counter',
    counterBet?: BetLevel,
  ) => {
    setError(null);
    try {
      const updated = await swipeService.respondToBetRequest(bet.id, action, counterBet);
      onResponded?.(updated);
    } catch (err: any) {
      logger.error('Failed to respond to bet request:', err);
      setError(err?.message || "That response couldn't be sent. Please try again.");
    }
  };

  return (
    <Panel>
      <Header>
        <BetIcon>{actionable ? '🔥' : '⏳'}</BetIcon>
        <HeaderText>
          <Title>{level} bet {iSent ? 'with' : 'from'} {otherName}</Title>
          <StatusLine $actionable={actionable}>
            {actionable
              ? 'Waiting on you — answer below'
              : `Waiting on ${otherName} to respond`}
          </StatusLine>
        </HeaderText>
      </Header>

      {message && (
        <Card>
          <CardLabel>{iSent ? 'What you sent' : `What ${otherName} said`}</CardLabel>
          <CardText>{message}</CardText>
        </Card>
      )}

      <MetaRow>
        <MetaItem>
          Sent
          <strong>{formatDateTime(bet.sentAt)}</strong>
        </MetaItem>
        <MetaItem>
          Expires
          <strong>{formatCountdown(bet.expiresAt)}</strong>
        </MetaItem>
        <MetaItem>
          Status
          <strong>{bet.status}</strong>
        </MetaItem>
      </MetaRow>

      {error && <ErrorNote>{error}</ErrorNote>}

      {actionable ? (
        // The real response UI, the same component the swipe hub uses — so
        // Fold / Call / Raise behave identically wherever a bet is answered.
        <MatchResponse matchRequest={bet} onResponse={handleResponse} />
      ) : (
        <WaitingNote>
          {otherName} has until {formatDateTime(bet.expiresAt)} to answer. As soon
          as they do, this becomes a conversation and it will appear in your
          messages — nothing else for you to do here.
        </WaitingNote>
      )}
    </Panel>
  );
};

export default BetDetail;
